import React, { useState, useMemo } from 'react';
import { X, Users, Lightbulb, Play, Settings2 } from 'lucide-react';
import { useI18n } from '../i18n';
import { formatMemberText } from '../locales/member';

/**
 * SetupDialog - Modal for configuring game setup parameters
 * Asks for player count, skill level, and optional game variants
 */
const SetupDialog = ({ 
    game, 
    onStart, 
    onCancel,
    maxPlayers = 12,
    minPlayers = 2
}) => {
    const { t } = useI18n();
    const [playerCount, setPlayerCount] = useState(4);
    const [skillLevel, setSkillLevel] = useState(2); // 0-5 scale
    const [selectedVariant, setSelectedVariant] = useState(null);

    const skillLevels = useMemo(() => [
        { level: 0, label: t.skillLevels.beginner, description: t.skillLevels.beginnerDesc, color: '#27ae60' },
        { level: 1, label: t.skillLevels.novice, description: t.skillLevels.noviceDesc, color: '#2ecc71' },
        { level: 2, label: t.skillLevels.hobbyist, description: t.skillLevels.hobbyistDesc, color: '#3498db' },
        { level: 3, label: t.skillLevels.experienced, description: t.skillLevels.experiencedDesc, color: '#9b59b6' },
        { level: 4, label: t.skillLevels.veteran, description: t.skillLevels.veteranDesc, color: '#e67e22' },
        { level: 5, label: t.skillLevels.pro, description: t.skillLevels.proDesc, color: '#e74c3c' }
    ], [t]);

    const commonVariants = useMemo(() => [
        { id: 'standard', label: t.gameVariants.standard, description: t.gameVariants.standardDesc },
        { id: 'quick', label: t.gameVariants.quick, description: t.gameVariants.quickDesc },
        { id: 'extended', label: t.gameVariants.extended, description: t.gameVariants.extendedDesc }
    ], [t]);

    const handleStart = () => {
        onStart({
            playerCount,
            skillLevel,
            variant: selectedVariant || 'standard'
        });
    };

    // Determine actual min/max from game data if available
    const actualMinPlayers = game?.minPlayers || minPlayers;
    const actualMaxPlayers = game?.maxPlayers || maxPlayers;

    return (
        <div className="setup-dialog-overlay" onClick={onCancel}>
            <div className="setup-dialog" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="setup-dialog-header">
                    <div className="header-content">
                        <Settings2 size={24} />
                        <div>
                            <h2>{t.setupDialog.title}</h2>
                            <p className="game-name">{game?.name || t.setupDialog.unknownGame}</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onCancel}>
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="setup-dialog-content">
                    {/* Player Count */}
                    <div className="form-section">
                        <label className="section-label">
                            <Users size={18} />
                            {t.setupDialog.playerCount}
                        </label>
                        <div className="player-count-selector">
                            {Array.from({ length: actualMaxPlayers - actualMinPlayers + 1 }).map((_, i) => {
                                const count = actualMinPlayers + i;
                                return (
                                    <button
                                        key={count}
                                        className={`player-btn ${playerCount === count ? 'active' : ''}`}
                                        onClick={() => setPlayerCount(count)}
                                    >
                                        {count}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="section-hint">
                            {formatMemberText(t.setupDialog.playerRange, {
                                game: game?.name || '',
                                min: actualMinPlayers,
                                max: actualMaxPlayers
                            })}
                        </p>
                    </div>

                    {/* Skill Level */}
                    <div className="form-section">
                        <label className="section-label">
                            <Lightbulb size={18} />
                            {t.setupDialog.skillLevel}
                        </label>
                        <div className="skill-slider-container">
                            <input
                                type="range"
                                min="0"
                                max="5"
                                value={skillLevel}
                                onChange={(e) => setSkillLevel(parseInt(e.target.value))}
                                className="skill-slider"
                                style={{
                                    '--slider-color': skillLevels[skillLevel].color
                                }}
                            />
                            <div className="skill-labels">
                                {skillLevels.map((skill) => (
                                    <span 
                                        key={skill.level}
                                        className={`skill-label ${skillLevel === skill.level ? 'active' : ''}`}
                                        onClick={() => setSkillLevel(skill.level)}
                                    >
                                        {skill.label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div 
                            className="skill-description"
                            style={{ borderColor: skillLevels[skillLevel].color }}
                        >
                            <span 
                                className="skill-badge"
                                style={{ background: skillLevels[skillLevel].color }}
                            >
                                {skillLevels[skillLevel].label}
                            </span>
                            <span>{skillLevels[skillLevel].description}</span>
                        </div>
                    </div>

                    {/* Game Variant (Optional) */}
                    <div className="form-section">
                        <label className="section-label">
                            <Settings2 size={18} />
                            {t.setupDialog.gameMode} <span className="optional">({t.common.optional})</span>
                        </label>
                        <div className="variant-options">
                            {commonVariants.map(variant => (
                                <button
                                    key={variant.id}
                                    className={`variant-btn ${selectedVariant === variant.id ? 'active' : ''}`}
                                    onClick={() => setSelectedVariant(
                                        selectedVariant === variant.id ? null : variant.id
                                    )}
                                >
                                    <span className="variant-label">{variant.label}</span>
                                    <span className="variant-desc">{variant.description}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="setup-dialog-footer">
                    <button className="btn-secondary" onClick={onCancel}>
                        {t.common.cancel}
                    </button>
                    <button className="btn-primary" onClick={handleStart}>
                        <Play size={18} />
                        {t.setupDialog.showSetup}
                    </button>
                </div>

                <style>{`
                    .setup-dialog-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.8);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 9998;
                        padding: 1rem;
                    }

                    .setup-dialog {
                        background: #161b22;
                        border-radius: 16px;
                        width: 100%;
                        max-width: 480px;
                        max-height: 90vh;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                        border: 1px solid #30363d;
                        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
                    }

                    .setup-dialog-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        padding: 1.25rem 1.5rem;
                        border-bottom: 1px solid #30363d;
                        background: rgba(0, 0, 0, 0.3);
                    }

                    .header-content {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        color: #fff;
                    }

                    .header-content h2 {
                        margin: 0;
                        font-size: 1.25rem;
                    }

                    .game-name {
                        margin: 0;
                        color: #8b949e;
                        font-size: 0.9rem;
                    }

                    .close-btn {
                        background: rgba(255, 255, 255, 0.1);
                        border: none;
                        color: #8b949e;
                        width: 32px;
                        height: 32px;
                        border-radius: 6px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s;
                    }

                    .close-btn:hover {
                        background: rgba(255, 255, 255, 0.2);
                        color: #fff;
                    }

                    .setup-dialog-content {
                        flex: 1;
                        overflow-y: auto;
                        padding: 1.5rem;
                    }

                    .form-section {
                        margin-bottom: 1.5rem;
                    }

                    .form-section:last-child {
                        margin-bottom: 0;
                    }

                    .section-label {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        color: #fff;
                        font-weight: 600;
                        margin-bottom: 0.75rem;
                    }

                    .optional {
                        font-weight: normal;
                        color: #8b949e;
                        font-size: 0.85rem;
                    }

                    .section-hint {
                        color: #8b949e;
                        font-size: 0.8rem;
                        margin-top: 0.5rem;
                    }

                    /* Player Count Selector */
                    .player-count-selector {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 0.5rem;
                    }

                    .player-btn {
                        width: 44px;
                        height: 44px;
                        border: 2px solid #30363d;
                        background: rgba(0, 0, 0, 0.3);
                        color: #c9d1d9;
                        border-radius: 8px;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s;
                    }

                    .player-btn:hover {
                        border-color: #58a6ff;
                        color: #fff;
                    }

                    .player-btn.active {
                        background: #58a6ff;
                        border-color: #58a6ff;
                        color: #fff;
                    }

                    /* Skill Slider */
                    .skill-slider-container {
                        margin-bottom: 0.75rem;
                    }

                    .skill-slider {
                        width: 100%;
                        height: 8px;
                        -webkit-appearance: none;
                        appearance: none;
                        background: #30363d;
                        border-radius: 4px;
                        outline: none;
                        margin-bottom: 0.5rem;
                    }

                    .skill-slider::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: var(--slider-color, #3498db);
                        cursor: pointer;
                        border: 3px solid #fff;
                        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                        transition: all 0.2s;
                    }

                    .skill-slider::-webkit-slider-thumb:hover {
                        transform: scale(1.1);
                    }

                    .skill-slider::-moz-range-thumb {
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: var(--slider-color, #3498db);
                        cursor: pointer;
                        border: 3px solid #fff;
                    }

                    .skill-labels {
                        display: flex;
                        justify-content: space-between;
                    }

                    .skill-label {
                        font-size: 0.7rem;
                        color: #8b949e;
                        cursor: pointer;
                        padding: 2px 4px;
                        border-radius: 4px;
                        transition: all 0.2s;
                    }

                    .skill-label:hover {
                        color: #c9d1d9;
                    }

                    .skill-label.active {
                        color: #fff;
                        font-weight: 600;
                    }

                    .skill-description {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        background: rgba(0, 0, 0, 0.3);
                        padding: 0.75rem;
                        border-radius: 8px;
                        border-left: 3px solid;
                        color: #c9d1d9;
                        font-size: 0.9rem;
                    }

                    .skill-badge {
                        padding: 0.25rem 0.5rem;
                        border-radius: 4px;
                        color: #fff;
                        font-size: 0.75rem;
                        font-weight: 600;
                        white-space: nowrap;
                    }

                    /* Variant Options */
                    .variant-options {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .variant-btn {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                        padding: 0.75rem 1rem;
                        background: rgba(0, 0, 0, 0.3);
                        border: 2px solid #30363d;
                        border-radius: 8px;
                        cursor: pointer;
                        transition: all 0.2s;
                        text-align: left;
                    }

                    .variant-btn:hover {
                        border-color: #58a6ff;
                    }

                    .variant-btn.active {
                        background: rgba(88, 166, 255, 0.1);
                        border-color: #58a6ff;
                    }

                    .variant-label {
                        color: #fff;
                        font-weight: 600;
                        font-size: 0.95rem;
                    }

                    .variant-desc {
                        color: #8b949e;
                        font-size: 0.8rem;
                        margin-top: 0.25rem;
                    }

                    /* Footer */
                    .setup-dialog-footer {
                        display: flex;
                        justify-content: flex-end;
                        gap: 0.75rem;
                        padding: 1rem 1.5rem;
                        border-top: 1px solid #30363d;
                        background: rgba(0, 0, 0, 0.3);
                    }

                    .btn-secondary {
                        padding: 0.625rem 1.25rem;
                        background: transparent;
                        border: 1px solid #30363d;
                        color: #c9d1d9;
                        border-radius: 8px;
                        font-size: 0.9rem;
                        cursor: pointer;
                        transition: all 0.2s;
                    }

                    .btn-secondary:hover {
                        background: rgba(255, 255, 255, 0.05);
                        border-color: #8b949e;
                    }

                    .btn-primary {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        padding: 0.625rem 1.25rem;
                        background: #238636;
                        border: none;
                        color: #fff;
                        border-radius: 8px;
                        font-size: 0.9rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.2s;
                    }

                    .btn-primary:hover {
                        background: #2ea043;
                    }

                    /* Mobile Responsive */
                    @media (max-width: 480px) {
                        .setup-dialog {
                            max-height: 100vh;
                            border-radius: 0;
                        }

                        .player-btn {
                            width: 38px;
                            height: 38px;
                            font-size: 0.9rem;
                        }

                        .skill-labels {
                            display: none;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default SetupDialog;
